import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Mail, Phone, MessageCircle, Trash2, Search, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Send, ArrowUpDown } from "lucide-react";
import { statusLabel, statusColor } from "@/lib/format";
import { toast } from "sonner";
import { openWa, waNumberWarning } from "@/lib/whatsapp";

const empty = { company: "", name: "", email: "", phone: "", whatsapp: "", notes: "", status: "attivo", tags: [], vat_number: "", tax_code: "", sdi_code: "", pec: "", address: "", city: "", zip: "", province: "" };

// Filter helper: "inattivo" also matches legacy "archiviato" records.
const statusMatches = (clientStatus, filter) => {
  if (filter === "all") return true;
  if (filter === "inattivo") return clientStatus === "inattivo" || clientStatus === "archiviato";
  return clientStatus === filter;
};

const initialsFor = (c) => {
  const src = (c.company || c.name || "?").trim();
  return src.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("updated_desc");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // CSV import
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => setClients((await api.get("/clients")).data);
  useEffect(() => { load(); }, []);

  const doImport = async () => {
    if (!importFile) return toast.error("Scegli un file CSV o Excel");
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/clients/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.imported} clienti importati`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore importazione"));
    } finally { setImporting(false); }
  };

  const resetImport = () => {
    setImportFile(null); setImportResult(null);
  };

  const openNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditingId(c.client_id); setOpen(true); };

  const canSave = (form.company || "").trim().length > 0;

  const save = async () => {
    if (!canSave) return toast.error("Il campo Azienda è obbligatorio");
    setSaving(true);
    try {
      if (editingId) await api.put(`/clients/${editingId}`, form);
      else await api.post("/clients", form);
      toast.success(editingId ? "Cliente aggiornato" : "Cliente creato");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo cliente?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente eliminato"); load();
  };

  const visibleClients = useMemo(() => {
    const searchLower = q.trim().toLowerCase();
    const list = clients.filter((c) => {
      if (!statusMatches(c.status, statusFilter)) return false;
      if (!searchLower) return true;
      return `${c.company || ""} ${c.name || ""} ${c.email || ""}`
        .toLowerCase()
        .includes(searchLower);
    });
    const ts = (c) => new Date(c.updated_at || c.created_at || 0).getTime();
    const cmp = {
      updated_desc: (a, b) => ts(b) - ts(a),
      updated_asc: (a, b) => ts(a) - ts(b),
      alpha_asc: (a, b) => (a.company || "").localeCompare(b.company || "", "it", { sensitivity: "base" }),
    }[sortBy] || ((a, b) => 0);
    return [...list].sort(cmp);
  }, [clients, q, statusFilter, sortBy]);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label">CRM</span>
          <h1 className="mt-2 font-heading text-3xl">Clienti</h1>
          <p className="text-zen-ink2 mt-1">{clients.length} contatti nel tuo database.</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zen-ink2"/>
            <Input data-testid="search-clients" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cerca…" className="pl-9 w-56"/>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="w-40"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="attivo">Attivi</SelectItem>
              <SelectItem value="inattivo">Inattivi</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="sort-clients" className="w-56">
              <ArrowUpDown size={14} className="text-zen-ink2 mr-1"/>
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated_desc">Più recenti</SelectItem>
              <SelectItem value="updated_asc">Meno recenti</SelectItem>
              <SelectItem value="alpha_asc">Azienda A → Z</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="import-clients-btn"
            variant="outline"
            onClick={() => { resetImport(); setImportOpen(true); }}
            className="rounded-full"
          >
            <Upload size={14} className="mr-1"/> Importa CSV
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="add-client-btn" className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
                <Plus size={16} className="mr-1"/> Nuovo cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
                <DialogDescription className="sr-only">Compila i campi del cliente e salva.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4">
                <div>
                  <Label>Azienda *</Label>
                  <Input
                    data-testid="cf-company"
                    value={form.company||""}
                    onChange={(e)=>setForm({...form, company:e.target.value})}
                    placeholder="Es. ACME Srl"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Nome referente <span className="text-xs text-zen-ink2 font-normal">(facoltativo)</span></Label>
                  <Input data-testid="cf-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})}/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Partita IVA <span className="text-xs text-zen-ink2 font-normal">(facoltativa)</span></Label>
                    <Input
                      data-testid="cf-vat"
                      placeholder="IT12345678901"
                      value={form.vat_number||""}
                      onChange={(e)=>setForm({...form, vat_number:e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Codice Fiscale <span className="text-xs text-zen-ink2 font-normal">(facoltativo)</span></Label>
                    <Input
                      data-testid="cf-tax-code"
                      placeholder="RSSMRA80A01H501U"
                      value={form.tax_code||""}
                      onChange={(e)=>setForm({...form, tax_code:e.target.value})}
                    />
                  </div>
                </div>
                <p className="text-xs text-zen-ink2 -mt-2">
                  Se compilati, verranno usati automaticamente nei PDF di fatture e preventivi.
                  Almeno uno tra P.IVA e Codice Fiscale è obbligatorio per generare la Fattura Elettronica (SdI).
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label>Indirizzo <span className="text-xs text-zen-ink2 font-normal">(via, n.)</span></Label>
                    <Input
                      data-testid="cf-address"
                      placeholder="Via Roma 12"
                      value={form.address||""}
                      onChange={(e)=>setForm({...form, address:e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>CAP</Label>
                    <Input
                      data-testid="cf-zip"
                      placeholder="20100"
                      maxLength={5}
                      value={form.zip||""}
                      onChange={(e)=>setForm({...form, zip:e.target.value.replace(/[^0-9]/g,"").slice(0,5)})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label>Comune</Label>
                    <Input
                      data-testid="cf-city"
                      placeholder="Milano"
                      value={form.city||""}
                      onChange={(e)=>setForm({...form, city:e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Prov.</Label>
                    <Input
                      data-testid="cf-province"
                      placeholder="MI"
                      maxLength={2}
                      value={form.province||""}
                      onChange={(e)=>setForm({...form, province:e.target.value.toUpperCase().replace(/[^A-Z]/g,"").slice(0,2)})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Codice Destinatario SDI <span className="text-xs text-zen-ink2 font-normal">(7 caratteri)</span></Label>
                    <Input
                      data-testid="cf-sdi-code"
                      placeholder="0000000"
                      maxLength={7}
                      value={form.sdi_code||""}
                      onChange={(e)=>setForm({...form, sdi_code:e.target.value.toUpperCase().slice(0,7)})}
                    />
                  </div>
                  <div>
                    <Label>PEC <span className="text-xs text-zen-ink2 font-normal">(alternativa al codice SDI)</span></Label>
                    <Input
                      data-testid="cf-pec"
                      type="email"
                      placeholder="cliente@pec.it"
                      value={form.pec||""}
                      onChange={(e)=>setForm({...form, pec:e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input data-testid="cf-email" type="email" value={form.email||""} onChange={(e)=>setForm({...form, email:e.target.value})}/></div>
                  <div><Label>Telefono</Label><Input data-testid="cf-phone" value={form.phone||""} onChange={(e)=>setForm({...form, phone:e.target.value})}/></div>
                </div>
                <div><Label>WhatsApp <span className="text-xs text-zen-ink2 font-normal">(con prefisso, es. +39 340 …)</span></Label>
                  <Input
                    data-testid="cf-whatsapp"
                    value={form.whatsapp||""}
                    onChange={(e)=>setForm({...form, whatsapp:e.target.value})}
                    placeholder="+39 340 1234567"
                  />
                  {form.whatsapp && waNumberWarning(form.whatsapp) && (
                    <div className="mt-1 flex items-start gap-1.5 text-xs text-zen-alert">
                      <AlertCircle size={12} className="mt-0.5 shrink-0"/>
                      <span>{waNumberWarning(form.whatsapp)}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Stato</Label>
                  <Select value={form.status === "archiviato" ? "inattivo" : form.status} onValueChange={(v)=>setForm({...form, status:v})}>
                    <SelectTrigger data-testid="cf-status"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="attivo">Attivo</SelectItem>
                      <SelectItem value="inattivo">Inattivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Note</Label><Textarea rows={3} data-testid="cf-notes" value={form.notes||""} onChange={(e)=>setForm({...form, notes:e.target.value})}/></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={()=>setOpen(false)}>Annulla</Button>
                <Button data-testid="cf-save" onClick={save} disabled={!canSave || saving} className="bg-zen-primary hover:bg-zen-primaryHover disabled:opacity-60">
                  {saving ? "Salvataggio…" : "Salva"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Importa clienti da CSV o Excel</DialogTitle>
            <DialogDescription>
              Carica un file .csv o .xlsx. La prima riga deve contenere i nomi delle colonne
              (accettiamo: <code>azienda, nome, email, telefono, whatsapp, note, stato, tag</code>
              — anche in inglese).
            </DialogDescription>
          </DialogHeader>
          {!importResult ? (
            <div className="space-y-4">
              <label className="block border-2 border-dashed border-zen-border rounded-xl p-8 text-center cursor-pointer hover:border-zen-primary/40 hover:bg-zen-bg/40 transition-colors">
                <FileSpreadsheet size={32} strokeWidth={1.3} className="mx-auto text-zen-primary/60 mb-2"/>
                <div className="text-sm text-zen-ink">
                  {importFile ? importFile.name : "Clicca o trascina un file CSV/XLSX qui"}
                </div>
                <div className="text-xs text-zen-ink2 mt-1">
                  {importFile
                    ? `${(importFile.size / 1024).toFixed(1)} KB`
                    : "Max 5MB · Colonne case-insensitive"}
                </div>
                <input
                  data-testid="import-file"
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                />
              </label>
              <div className="text-xs text-zen-ink2">
                <b>Esempio CSV:</b>
                <pre className="bg-zen-bg p-2 rounded mt-1 overflow-x-auto">azienda,nome,email,telefono,stato{"\n"}ACME Srl,Mario Rossi,mario@acme.it,+39 333 1234567,attivo</pre>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="import-result">
              <div className="p-4 rounded-xl bg-zen-primary/10 border border-zen-primary/30 flex items-start gap-3">
                <CheckCircle2 size={20} className="text-zen-primary shrink-0 mt-0.5"/>
                <div className="flex-1 text-sm">
                  <div className="font-medium text-zen-primary">{importResult.imported} clienti importati</div>
                  <div className="text-zen-ink2 mt-1">
                    {importResult.imported === 0
                      ? "Verifica che l'header contenga 'azienda' o 'company'."
                      : "I nuovi clienti sono ora nella tua rubrica."}
                  </div>
                </div>
              </div>
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="p-4 rounded-xl bg-zen-alert/5 border border-zen-alert/30" data-testid="import-errors">
                  <div className="flex items-center gap-2 text-zen-alert font-medium text-sm mb-2">
                    <AlertCircle size={16}/> {importResult.errors.length} righe non importate
                  </div>
                  <ul className="text-xs text-zen-ink2 space-y-1 max-h-48 overflow-y-auto">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>· Riga {err.row}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {importResult ? (
              <>
                <Button variant="ghost" onClick={resetImport}>Importa altro file</Button>
                <Button onClick={() => setImportOpen(false)} className="bg-zen-primary hover:bg-zen-primaryHover">Chiudi</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setImportOpen(false)}>Annulla</Button>
                <Button
                  data-testid="import-run"
                  onClick={doImport}
                  disabled={!importFile || importing}
                  className="bg-zen-primary hover:bg-zen-primaryHover"
                >
                  {importing ? "Importazione…" : "Importa"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {visibleClients.length === 0 ? (
        <div className="zen-card p-12 text-center text-zen-ink2">
          {clients.length === 0
            ? "Nessun cliente trovato. Aggiungi il primo!"
            : "Nessun cliente corrisponde ai filtri attuali."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visibleClients.map((c) => (
            <div key={c.client_id} data-testid={`client-card-${c.client_id}`} className="zen-card p-5 group">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="w-11 h-11">
                    <AvatarFallback className="bg-zen-subtle/30 text-zen-ink">
                      {initialsFor(c)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <button
                      onClick={()=>openEdit(c)}
                      className="font-medium text-zen-ink hover:text-zen-primary text-left truncate block max-w-full"
                      data-testid={`client-company-${c.client_id}`}
                    >
                      {c.company || "—"}
                    </button>
                    {c.name && <div className="text-xs text-zen-ink2 truncate" data-testid={`client-name-${c.client_id}`}>{c.name}</div>}
                  </div>
                </div>
                <Badge className={`${statusColor[c.status] || statusColor.attivo} rounded-full font-normal shrink-0`}>
                  {statusLabel[c.status] || c.status}
                </Badge>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-zen-ink2">
                {c.email && <div className="flex items-center gap-2 truncate"><Mail size={13}/> {c.email}</div>}
                {c.phone && <div className="flex items-center gap-2"><Phone size={13}/> {c.phone}</div>}
                {c.whatsapp && (
                  <div className="flex items-center gap-2 min-w-0">
                    <MessageCircle size={13} className="shrink-0"/>
                    <span className="truncate">{c.whatsapp}</span>
                    <button
                      data-testid={`wa-open-${c.client_id}`}
                      onClick={(e)=>{
                        e.stopPropagation();
                        const ok = openWa(c.whatsapp);
                        if (!ok) toast.error(waNumberWarning(c.whatsapp));
                      }}
                      title="Apri chat WhatsApp"
                      className="ml-auto shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-zen-primary/10 text-zen-primary hover:bg-zen-primary/20 border border-zen-primary/20 transition-colors"
                    >
                      <Send size={11}/> Chat
                    </button>
                  </div>
                )}
              </div>
              {c.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {c.tags.map(t => <Badge key={t} variant="secondary" className="bg-zen-subtle/25 text-zen-ink font-normal">{t}</Badge>)}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-zen-border flex justify-between items-center">
                <button onClick={()=>openEdit(c)} className="text-sm text-zen-primary hover:underline">Modifica</button>
                <button data-testid={`del-client-${c.client_id}`} onClick={()=>remove(c.client_id)} className="text-zen-alert hover:text-zen-alert/80 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={15}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
